import React, { useState } from "react";
import { ArrowLeft, ArrowRight, CheckCircle2, CircleDollarSign, CircleX, UserRound } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";

const countries = [
  "France", "United States", "Canada", "United Kingdom", "Senegal", "Cote d'Ivoire",
  "Cameroon", "Mali", "Guinea", "Morocco", "Algeria", "Tunisia", "Belgium",
  "Germany", "Spain", "Italy", "Portugal", "Nigeria", "Ghana", "Other",
];

const categories = [
  { value: "student", detail: "student" },
  { value: "salaried_diaspora", detail: "salaried" },
  { value: "community_family", detail: "community" },
  { value: "entrepreneur_business", detail: "business" },
];

const phonePrefixes = {
  France: "+33",
  "United States": "+1",
  Canada: "+1",
  "United Kingdom": "+44",
  Senegal: "+221",
  "Cote d'Ivoire": "+225",
  Cameroon: "+237",
  Mali: "+223",
  Guinea: "+224",
  Morocco: "+212",
  Algeria: "+213",
  Tunisia: "+216",
  Belgium: "+32",
  Germany: "+49",
  Spain: "+34",
  Italy: "+39",
  Portugal: "+351",
  Nigeria: "+234",
  Ghana: "+233",
};

const Join = () => {
  const { t, i18n } = useTranslation();
  const { register } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
    country: "",
    phone: "",
    category: "salaried_diaspora",
    preferredAmount: "100",
    agree: false,
    kycDocument: null,
  });
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const passwordsMatch = Boolean(form.confirmPassword) && form.password === form.confirmPassword;
  const passwordMismatch = Boolean(form.confirmPassword) && form.password !== form.confirmPassword;
  const phonePlaceholder = phonePrefixes[form.country] || t("auth.phonePlaceholder");
  const update = (event) => setForm({
    ...form,
    [event.target.name]: event.target.type === "checkbox" ? event.target.checked : event.target.value,
  });

  const passwordStrength = (() => {
    if (!form.password) return { label: "none", width: 0, color: "bg-gray-200" };
    if (form.password.length >= 12 && /[A-Z]/.test(form.password) && /\d/.test(form.password)) {
      return { label: "strong", width: 100, color: "bg-emerald-500" };
    }
    if (form.password.length >= 8) return { label: "good", width: 70, color: "bg-gold-500" };
    return { label: "weak", width: 35, color: "bg-red-400" };
  })();

  const next = async (event) => {
    event.preventDefault();
    setError("");
    if (step === 0) {
      if (form.password !== form.confirmPassword) return setError(t("auth.signup.passwordMismatch"));
      if (form.password.length < 8) return setError(t("auth.signup.passwordTooShort"));
      setStep(1);
      return;
    }
    if (!form.agree) return setError(t("auth.signup.acceptPolicies"));
    if (form.phone.trim() && !/^\+[1-9]\d{7,14}$/.test(form.phone.trim())) return setError(t("auth.signup.phoneInvalid"));
    if (!form.kycDocument) return setError(t("auth.signup.uploadDocument"));
    setSubmitting(true);
    try {
      await register({
        name: form.name,
        email: form.email,
        password: form.password,
        phone: form.phone,
        country: form.country,
        category: form.category,
        preferredAmount: Number(form.preferredAmount),
        preferredLanguage: i18n.resolvedLanguage === "fr" ? "fr" : "en",
        kycDocument: form.kycDocument,
      });
      navigate("/dashboard");
    } catch (err) {
      setError(err.response?.data?.message || (err.request
        ? t("auth.signup.serverUnavailable")
        : t("auth.signup.createError")));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="page-shell max-w-5xl py-6 sm:py-10">
      <div className="grid items-start gap-6 lg:grid-cols-[0.8fr_1.2fr]">
        <aside className="signup-intro-panel relative isolate overflow-hidden rounded-3xl px-6 py-9 text-white shadow-xl sm:px-10 sm:py-12">
          <svg aria-hidden="true" viewBox="0 0 200 200" className="pointer-events-none absolute right-5 top-5 h-28 w-28 text-gold-400 opacity-15 sm:h-36 sm:w-36">
            <circle cx="100" cy="100" r="78" fill="none" stroke="currentColor" strokeWidth="1.5" />
            <circle cx="100" cy="100" r="52" fill="none" stroke="white" strokeOpacity=".45" strokeWidth="1" strokeDasharray="3 7" />
            <path d="M100 22v26m0 104v26M22 100h26m104 0h26" fill="none" stroke="currentColor" strokeWidth="1.5" />
            <circle cx="100" cy="22" r="5" fill="currentColor" />
            <circle cx="178" cy="100" r="5" fill="currentColor" />
            <circle cx="100" cy="178" r="5" fill="currentColor" />
            <circle cx="22" cy="100" r="5" fill="currentColor" />
            <circle cx="100" cy="100" r="12" fill="currentColor" fillOpacity=".28" stroke="currentColor" strokeWidth="1.5" />
          </svg>
          <div className="relative z-10">
            <p className="eyebrow text-gold-400">{t("auth.signup.eyebrow")}</p>
            <h1 className="mt-4 font-serif text-4xl font-bold">{t("auth.signup.title")}</h1>
            <p className="mt-4 text-sm leading-7 text-green-100">{t("auth.signup.intro")}</p>
            <div className="mt-8 space-y-3">
              {[
                ["stepAccount", "stepAccountDetail", UserRound],
                ["stepCircle", "stepCircleDetail", CircleDollarSign],
              ].map(([titleKey, detailKey, Icon], index) => (
                <div className={`flex gap-3 rounded-xl p-3 transition ${index === step ? "bg-white/15" : "opacity-60"}`} key={titleKey}>
                  <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${index <= step ? "bg-gold-400 text-forest-deep" : "border border-white/20 text-white/60"}`}><Icon size={16} /></span>
                  <div><p className="text-sm font-semibold">{t(`auth.signup.${titleKey}`)}</p><p className="mt-0.5 text-xs text-green-100/70">{t(`auth.signup.${detailKey}`)}</p></div>
                </div>
              ))}
            </div>
          </div>
        </aside>

        <section className="card self-start p-6 sm:p-8">
          <div className="flex items-center justify-between">
            <div>
              <p className="eyebrow">{t("auth.signup.step", { current: step + 1 })}</p>
              <h2 className="mt-2 font-serif text-3xl font-bold text-forest">{t(step === 0 ? "auth.signup.createAccount" : "auth.signup.chooseCircle")}</h2>
            </div>
            <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gold-50 text-gold-600">{step === 0 ? <UserRound size={23} /> : <CircleDollarSign size={23} />}</span>
          </div>
          <div className="mt-6 h-2 overflow-hidden rounded-full bg-gold-50"><div className="h-full rounded-full bg-gold-600 transition-all" style={{ width: `${((step + 1) / 2) * 100}%` }} /></div>
          {error && <p role="alert" className="mt-6 rounded-xl border border-red-100 bg-red-50 p-3 text-sm text-red-700">{error}</p>}

          <form onSubmit={next} className="join-form mt-8 space-y-5">
            {step === 0 && <>
              <div className="grid gap-4 sm:grid-cols-2">
                <div><label className="label">{t("auth.name")}</label><input className="input !border-gray-200 !bg-white" name="name" required value={form.name} onChange={update} placeholder={t("auth.signup.fullNamePlaceholder")} /></div>
                <div><label className="label">{t("auth.country")}</label><select className="input !border-gray-200 !bg-white" name="country" required value={form.country} onChange={update}><option value="">{t("auth.signup.selectCountry")}</option>{countries.map((country) => <option value={country} key={country}>{t(`countries.${country}`)}</option>)}</select></div>
              </div>
              <div><label className="label">{t("auth.email")}</label><input className="input !border-gray-200 !bg-white" type="email" name="email" required value={form.email} onChange={update} placeholder={t("common.emailPlaceholder")} /></div>
              <div><label className="label">{t("auth.phone")} ({t("auth.signup.optional")})</label><input className="input !border-gray-200 !bg-white" type="tel" name="phone" value={form.phone} onChange={update} placeholder={phonePlaceholder} /><p className="mt-1 text-xs text-gray-500">{t("auth.signup.phoneFormat")}</p></div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="label">{t("auth.password")}</label>
                  <input className="input !border-gray-200 !bg-white" type="password" name="password" required minLength={8} value={form.password} onChange={update} placeholder={t("auth.signup.passwordMin")} />
                  <div className="mt-2"><div className="h-2 overflow-hidden rounded-full bg-gray-100"><div className={`h-full rounded-full ${passwordStrength.color}`} style={{ width: `${passwordStrength.width}%` }} /></div><p className="mt-2 text-xs text-gray-500">{t("auth.signup.passwordStrength")}: <span className="font-semibold text-gray-700">{t(`auth.signup.strength.${passwordStrength.label}`)}</span></p></div>
                </div>
                <div>
                  <label className="label">{t("auth.confirmPassword")}</label>
                  <div className="relative"><input className="input !border-gray-200 !bg-white pr-10" type="password" name="confirmPassword" required value={form.confirmPassword} onChange={update} placeholder={t("auth.signup.repeatPassword")} />{passwordsMatch && <CheckCircle2 aria-label={t("auth.passwordsMatch")} className="absolute right-3 top-1/2 -translate-y-1/2 text-emerald-600" size={18} />}{passwordMismatch && <CircleX aria-label={t("auth.passwordsMismatch")} className="absolute right-3 top-1/2 -translate-y-1/2 text-red-600" size={18} />}</div>
                  {passwordsMatch && <p role="status" className="mt-1 text-xs text-emerald-700">{t("auth.passwordsMatch")}</p>}
                  {passwordMismatch && <p role="alert" className="mt-1 text-xs text-red-700">{t("auth.passwordsMismatch")}</p>}
                </div>
              </div>
            </>}

            {step === 1 && <>
              <div><label className="label">{t("auth.signup.chooseCategory")}</label><div className="space-y-3">{categories.map((category) => <label className={`flex cursor-pointer items-center justify-between rounded-xl border p-4 ${form.category === category.value ? "border-gold-400 bg-gold-50" : "border-gray-200"}`} key={category.value}><span><span className="block font-semibold text-forest">{t(`tontine.categories.${category.value}`)}</span><span className="mt-1 block text-xs text-gray-500">{t(`auth.signup.categoryDetail.${category.detail}`)}</span></span><input type="radio" name="category" value={category.value} checked={form.category === category.value} onChange={update} className="accent-gold-600" /></label>)}</div></div>
              <div><label className="label">{t("auth.signup.preferredAmount")}</label><select className="input" name="preferredAmount" value={form.preferredAmount} onChange={update}>{[25, 50, 100, 250, 500].map((amount) => <option value={amount} key={amount}>{t("auth.signup.perCycle", { amount })}</option>)}</select></div>
              <div className="rounded-xl border border-gold-100 bg-gold-50/60 p-4"><label className="label">{t("auth.signup.identityDocument")}</label><input className="input bg-white" type="file" accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png" required onChange={(event) => setForm({ ...form, kycDocument: event.target.files[0] || null })} /><p className="mt-2 text-xs leading-5 text-gray-500">{t("auth.signup.identityInstructions")}</p></div>
              <label className="flex items-start gap-2 text-xs leading-5 text-gray-500"><input type="checkbox" name="agree" checked={form.agree} onChange={update} className="mt-1 accent-gold-600" /><span>{t("auth.signup.termsAgreement")} <Link to="/terms" className="font-semibold text-gold-700">{t("auth.signup.terms")}</Link> {t("auth.signup.and")} <Link to="/privacy" className="font-semibold text-gold-700">{t("auth.signup.privacyPolicy")}</Link>.</span></label>
              <div className="rounded-xl bg-emerald-50 p-4 text-sm text-emerald-800"><CheckCircle2 className="mr-2 inline" size={17} />{t("auth.signup.documentReview")}</div>
            </>}

            <div className="flex justify-between gap-3 pt-3">
              {step > 0 ? <button type="button" onClick={() => { setError(""); setStep(0); }} className="btn-secondary"><ArrowLeft size={16} /> {t("auth.signup.back")}</button> : <span />}
              <button className="btn-primary" type="submit" disabled={submitting}>{submitting ? t("auth.signup.creating") : step === 1 ? t("auth.signup.createButton") : t("auth.signup.continue")} <ArrowRight size={16} /></button>
            </div>
          </form>
          <p className="mt-6 text-center text-sm text-gray-500">{t("auth.haveAccount")} <Link to="/login" className="font-semibold text-gold-700 hover:underline">{t("nav.login")}</Link></p>
        </section>
      </div>
    </main>
  );
};

export default Join;
